import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import PhotoUpload from "../../../bar-owner/components/management/PhotoUpload";
import PhotoUploadProgressOverlay from "../../../bar-owner/components/management/PhotoUploadProgressOverlay";
import GenreMultiSelect from "../GenreMultiSelect";
import { post } from "../../../../services/api";
import { uploadPhotoFileWithGuards } from "../../../../services/uploadPhoto";

function photoItemsToUrls(items, newUrls) {
  let i = 0;
  return items.map((item) =>
    item.type === "url" ? item.url : newUrls[i++]
  );
}

// Try to import useArtist - will only work if within ArtistProvider
let useArtist = null;
try {
  const artistContext = require("../../../../context/ArtistContext");
  useArtist = artistContext.useArtist;
} catch (e) {
  // Context not available - that's okay
}

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
  marginTop: "10px",
};

const defaultCenter = {
  lat: 24.5551, // Key West, FL
  lng: -81.78,
};

const CreateArtistModal = ({
  isOpen,
  onClose,
  onSuccess,
  isMapsLoaded = false,
}) => {
  // Try to use ArtistContext if available
  let artistContext = null;
  if (useArtist) {
    try {
      artistContext = useArtist();
    } catch (e) {
      // Not within ArtistProvider - that's okay
    }
  }

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [uploadOverlay, setUploadOverlay] = useState({
    visible: false,
    total: 0,
    current: -1,
    results: [],
  });
  const [rejectedPhotoIndices, setRejectedPhotoIndices] = useState([]);
  const uploadAbortRef = useRef(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapZoom, setMapZoom] = useState(13);
  const [formData, setFormData] = useState({
    stage_name: "",
    hometown: "",
    latitude: null,
    longitude: null,
    facebook: "",
    instagram: "",
    website: "",
    venmo: "",
    artist_pic: null,
    description: "",
    genres: [],
    type: "",
    photos: [], // For PhotoUpload component
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        stage_name: "",
        hometown: "",
        latitude: null,
        longitude: null,
        facebook: "",
        instagram: "",
        website: "",
        venmo: "",
        artist_pic: null,
        description: "",
        genres: [],
        type: "",
        photos: [],
      });
      setMapCenter(defaultCenter);
      setMarkerPosition(null);
      setMapZoom(13);
      setAutocomplete(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhotosChange = (items) => {
    setRejectedPhotoIndices([]);
    setFormData({ ...formData, photos: items });
  };

  const handleRemovePhoto = (index) => {
    if (formData.photos.length <= 1) {
      setFormError(
        "You need at least one photo. Add a new one first, then you can remove this one."
      );
      return;
    }
    const updated = formData.photos.filter((_, i) => i !== index);
    setRejectedPhotoIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
    setFormData({ ...formData, photos: updated });
    setFormError("");
  };

  const onLoadAutocomplete = (autocompleteObj) => {
    setAutocomplete(autocompleteObj);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setFormData({
          ...formData,
          latitude: lat,
          longitude: lng,
        });

        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setMapZoom(14); // Zoom in when address is selected
      }
    }
  };

  const onMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setFormData({
      ...formData,
      latitude: lat,
      longitude: lng,
    });

    setMarkerPosition({ lat, lng });
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.stage_name.trim()) {
      setFormError("Please enter your stage name.");
      return;
    }

    if (!formData.hometown.trim()) {
      setFormError("Please enter your hometown.");
      return;
    }

    if (!formData.type) {
      setFormError("Please select your artist type (Solo, Duo, or Band).");
      return;
    }

    if (!formData.genres || formData.genres.length < 1) {
      setFormError("Please select at least one genre.");
      return;
    }

    if (!formData.description.trim()) {
      setFormError("Please enter a description.");
      return;
    }

    if (formData.photos.length < 1) {
      setFormError("Please add at least one photo.");
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      setFormError(
        "Please select a location on the map. This helps bars find you and helps you find new bars."
      );
      return;
    }

    const fileItems = formData.photos.filter((i) => i.type === "file");
    let finalPhotoUrls = [];

    if (fileItems.length > 0) {
      uploadAbortRef.current = new AbortController();
      setUploadOverlay({
        visible: true,
        total: fileItems.length,
        current: -1,
        results: [],
      });
      const results = [];
      const newUrls = [];

      try {
        for (let i = 0; i < fileItems.length; i++) {
          let data;
          try {
            data = await uploadPhotoFileWithGuards(fileItems[i].file, {
              batchSignal: uploadAbortRef.current.signal,
            });
          } catch (uploadErr) {
            if (uploadErr.code === "CANCELLED") {
              setFormError("Upload cancelled.");
            } else if (uploadErr.code === "TIMEOUT") {
              setFormError(uploadErr.message);
            } else {
              setFormError(
                uploadErr.message ||
                  "Something went wrong uploading photos. Please try again."
              );
            }
            return;
          }
          const ok = data.ok && data.url;
          results.push(ok ? "success" : "rejected");
          if (ok) newUrls.push(data.url);
          setUploadOverlay((prev) => ({
            ...prev,
            current: i,
            results: [...results],
          }));
        }
      } finally {
        setUploadOverlay((prev) => ({ ...prev, visible: false }));
        uploadAbortRef.current = null;
      }

      const rejectedOneBased = results
        .map((r, i) => (r === "rejected" ? i + 1 : null))
        .filter((x) => x != null);
      if (rejectedOneBased.length > 0) {
        const formIndices = [];
        let fi = 0;
        let successIdx = 0;
        const newPhotos = formData.photos.map((item) => {
          if (item.type === "url") return item;
          const r = results[fi++];
          if (r === "success") {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            return { type: "url", url: newUrls[successIdx++] };
          }
          return item;
        });
        fi = 0;
        for (let i = 0; i < formData.photos.length; i++) {
          if (formData.photos[i].type === "file") {
            if (results[fi++] === "rejected") formIndices.push(i);
          }
        }
        setFormData({ ...formData, photos: newPhotos });
        setRejectedPhotoIndices(formIndices);
        setFormError(
          `Photo${rejectedOneBased.length > 1 ? "s" : ""} ${rejectedOneBased.join(" and ")} ${rejectedOneBased.length > 1 ? "were" : "was"} rejected. Please remove or replace ${rejectedOneBased.length > 1 ? "them" : "it"}.`
        );
        return;
      }

      finalPhotoUrls = photoItemsToUrls(formData.photos, newUrls);
    } else {
      finalPhotoUrls = formData.photos.map((i) => i.url);
    }

    setIsSubmitting(true);

    try {
      const artistData = {
        stage_name: formData.stage_name.trim(),
        hometown: formData.hometown.trim(),
        latitude: formData.latitude,
        longitude: formData.longitude,
        facebook: formData.facebook.trim() || null,
        instagram: formData.instagram.trim() || null,
        website: formData.website.trim() || null,
        venmo: formData.venmo.trim() || null,
        artist_photos: finalPhotoUrls,
        artist_pic: finalPhotoUrls[0],
        description: formData.description.trim(),
        genres: formData.genres,
        type: formData.type,
      };

      const data = await post("/artists", artistData);

      if (data.success) {
        if (artistContext?.refreshArtists) {
          await artistContext.refreshArtists();
        }
        if (onSuccess) {
          onSuccess(data.artist);
        }
        handleClose();
      } else {
        setFormError(data.error || "Something went wrong. Please try again.");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Create artist error:", error);
      setFormError("Failed to create artist profile. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <PhotoUploadProgressOverlay
        visible={uploadOverlay.visible}
        total={uploadOverlay.total}
        current={uploadOverlay.current}
        results={uploadOverlay.results}
        onCancel={
          uploadOverlay.visible
            ? () => uploadAbortRef.current?.abort()
            : undefined
        }
      />
    <div
      className="artist-profile-modal-overlay"
      role="presentation"
    >
      <div
        className="artist-profile-modal groups-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-artist-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "900px", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="artist-profile-modal-header">
          <h2 id="create-artist-modal-title">Create New Artist Group</h2>
          <button
            type="button"
            className="artist-profile-modal-close"
            aria-label="Close"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="artist-profile-modal-body groups-modal-body">
          <form onSubmit={handleSubmit} className="onboarding-form">
            <div className="onboarding-section">
              <h2 className="section-title">Artist Information</h2>

              <div className="form-grid">
                <div className="input-group">
                  <label className="input-label">Stage Name *</label>
                  <input
                    type="text"
                    name="stage_name"
                    value={formData.stage_name}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Enter your stage name or band name"
                    required
                    maxLength={128}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Hometown *</label>
                  <input
                    type="text"
                    name="hometown"
                    value={formData.hometown}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="e.g., Key West, FL"
                    required
                    maxLength={128}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="settings-input"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select a type...</option>
                    <option value="Solo">Solo</option>
                    <option value="Duo">Duo</option>
                    <option value="Band">Band</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Genres *</label>
                  <GenreMultiSelect
                    value={formData.genres}
                    onChange={(genres) =>
                      setFormData((prev) => ({ ...prev, genres }))
                    }
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-group full-width">
                  <label className="input-label">Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Tell us about your music style, etc."
                    required
                    maxLength={255}
                    rows={3}
                    disabled={isSubmitting}
                  />
                  <p
                    className="input-hint"
                    style={{
                      marginTop: "12px",
                      fontSize: "18px",
                      color: "#ffffff",
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    You will be able to invite other musicians to your
                    group/band inside the app 👍
                  </p>
                </div>
              </div>
            </div>

            <div className="onboarding-section">
              <h2 className="section-title">Location *</h2>
              <p
                className="section-description"
                style={{ marginBottom: "15px" }}
              >
                <strong>No one will ever see this.</strong> It is used to help
                bars find you and you find new bars. Place in a central location
                you want to find gigs.
              </p>

              <div className="form-grid">
                <div className="input-group full-width">
                  <label className="input-label">
                    {formData.latitude && formData.longitude
                      ? "Finalize Location (Drag Marker) *"
                      : "Search for a city or town *"}
                  </label>
                  {isMapsLoaded ? (
                    <Autocomplete
                      onLoad={onLoadAutocomplete}
                      onPlaceChanged={onPlaceChanged}
                    >
                      <input
                        type="text"
                        className="settings-input"
                        placeholder="Start typing a location..."
                        disabled={isSubmitting}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      className="settings-input"
                      placeholder="Loading map..."
                      disabled={true}
                    />
                  )}
                  <p className="input-hint">
                    Select from dropdown suggestions, then adjust the map marker
                    if needed
                  </p>
                </div>

                {/* Map */}
                <div className="input-group full-width">
                  <label className="input-label">
                    {formData.latitude && formData.longitude
                      ? "Finalize Location (Drag Marker) *"
                      : "Select a location to place marker *"}
                  </label>
                  {isMapsLoaded ? (
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={mapZoom}
                      options={{ gestureHandling: "cooperative" }}
                    >
                      {formData.latitude && formData.longitude && (
                        <Marker
                          position={
                            markerPosition || {
                              lat: formData.latitude,
                              lng: formData.longitude,
                            }
                          }
                          draggable={true}
                          onDragEnd={onMarkerDragEnd}
                        />
                      )}
                    </GoogleMap>
                  ) : (
                    <div
                      style={{
                        ...mapContainerStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f0f0f0",
                        color: "#666",
                      }}
                    >
                      Loading map...
                    </div>
                  )}
                  {formData.latitude && formData.longitude && (
                    <p className="input-hint">
                      Coordinates: {formData.latitude.toFixed(6)},{" "}
                      {formData.longitude.toFixed(6)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="onboarding-section">
              <h2 className="section-title">Social Media & Links (Optional)</h2>
              <p className="section-description">
                Share your social media profiles and website so venues can learn
                more about you
              </p>

              <div className="form-grid">
                <div className="input-group">
                  <label className="input-label">Facebook</label>
                  <input
                    type="text"
                    name="facebook"
                    value={formData.facebook}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Facebook profile or page URL"
                    maxLength={128}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Instagram</label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Instagram handle or URL"
                    maxLength={128}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Website</label>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Your website URL"
                    maxLength={128}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Venmo</label>
                  <input
                    type="text"
                    name="venmo"
                    value={formData.venmo}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Venmo username"
                    maxLength={128}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="onboarding-section">
              <h2 className="section-title">Profile Photo</h2>
              <p className="section-description">
                Add at least one photo (up to 5). First photo is your primary.
              </p>

              <PhotoUpload
                label="Artist Photos"
                value={formData.photos}
                onChange={handlePhotosChange}
                onRemove={handleRemovePhoto}
                minPhotos={1}
                maxPhotos={5}
                rejectedIndices={rejectedPhotoIndices}
              />
            </div>

            {formError && (
              <div className="error-message" role="alert">
                <p>{formError}</p>
              </div>
            )}
            <div className="onboarding-actions">
              <button
                type="submit"
                className="submit-btn"
                disabled={
                  isSubmitting ||
                  !formData.stage_name.trim() ||
                  !formData.hometown.trim() ||
                  !formData.type ||
                  !formData.genres?.length ||
                  !formData.description.trim() ||
                  !formData.latitude ||
                  !formData.longitude
                }
              >
                {isSubmitting ? "Creating Profile..." : "Create Artist Profile"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default CreateArtistModal;
